import type { User } from 'firebase/auth'
import type { Translation } from '../../../translations'
import { ChatUserMenu } from './ChatUserMenu'

interface ChatComposerProps {
    input: string
    onInputChange: (value: string) => void
    onInputFocus: () => void
    onInputBlur: () => void
    onInputKeyDown: (event: React.KeyboardEvent<HTMLInputElement>) => void
    inputRef: React.MutableRefObject<HTMLInputElement | null>
    chatSuggestions: string[]
    isSuggestOpen: boolean
    suggestIndex: number
    onAcceptSuggestion: (value: string) => void
    onSend: () => void
    onClearChat: () => void
    isLoading: boolean
    placeholder: string
    t: Translation
    user: User | null
    onLogin: () => Promise<void>
}

export function ChatComposer({
    input,
    onInputChange,
    onInputFocus,
    onInputBlur,
    onInputKeyDown,
    inputRef,
    chatSuggestions,
    isSuggestOpen,
    suggestIndex,
    onAcceptSuggestion,
    onSend,
    onClearChat,
    isLoading,
    placeholder,
    t,
    user,
    onLogin,
}: ChatComposerProps) {
    return (
        <div className="bg-gradient-to-t from-white via-white to-transparent pt-4">
            <div className="mx-auto w-full max-w-6xl">
                <div className="mb-2 flex items-center justify-between gap-2 px-1">
                    <button
                        type="button"
                        onClick={onClearChat}
                        className="rounded-full border border-slate-200 bg-white p-2 text-slate-700 transition-colors hover:bg-slate-50 disabled:opacity-50 shadow-sm"
                        disabled={isLoading}
                        aria-label={t.chatClear}
                        title={t.chatClear}
                    >
                        <svg
                            xmlns="http://www.w3.org/2000/svg"
                            viewBox="0 0 24 24"
                            fill="currentColor"
                            className="h-4 w-4"
                            aria-hidden="true"
                        >
                            <path d="M6 19c0 1.1.9 2 2 2h8c1.1 0 2-.9 2-2V7H6v12zM19 4h-3.5l-1-1h-5l-1 1H5v2h14V4z" />
                        </svg>
                    </button>

                    <ChatUserMenu
                        user={user}
                        onLogin={onLogin}
                        t={t}
                    />
                </div>

                <div className="flex gap-2 rounded-2xl bg-slate-50 p-2 shadow-inner border border-slate-200">
                    <div className="relative flex-1">
                        <input
                            type="text"
                            value={input}
                            onChange={(event) => onInputChange(event.target.value)}
                            onFocus={onInputFocus}
                            onBlur={onInputBlur}
                            onKeyDown={onInputKeyDown}
                            placeholder={placeholder}
                            className="w-full bg-transparent px-4 py-3 outline-none placeholder:text-slate-400"
                            disabled={isLoading}
                            ref={inputRef}
                        />

                        {isSuggestOpen && chatSuggestions.length > 0 && (
                            <div className="absolute bottom-full z-30 mb-4 w-full overflow-hidden rounded-xl border border-slate-200 bg-white shadow-lg">
                                {chatSuggestions.map((suggestion, idx) => (
                                    <button
                                        key={`${suggestion}-${idx}`}
                                        type="button"
                                        onMouseDown={(event) => event.preventDefault()}
                                        onClick={() => onAcceptSuggestion(suggestion)}
                                        className={`w-full px-4 py-3 text-left text-sm ${idx === suggestIndex ? 'bg-slate-50' : 'bg-white'} hover:bg-slate-50 border-b border-slate-100 last:border-0`}
                                    >
                                        {suggestion}
                                    </button>
                                ))}
                            </div>
                        )}
                    </div>

                    <button
                        type="button"
                        onClick={onSend}
                        disabled={isLoading || !input.trim()}
                        className="rounded-xl bg-blue-600 p-3 font-semibold text-white shadow-sm transition-all hover:bg-blue-700 disabled:opacity-50 disabled:shadow-none"
                        aria-label={t.chatSend}
                        title={t.chatSend}
                    >
                        <svg
                            xmlns="http://www.w3.org/2000/svg"
                            viewBox="0 0 24 24"
                            fill="currentColor"
                            className="h-5 w-5"
                            aria-hidden="true"
                        >
                            <path d="M2.01 21L23 12 2.01 3 2 10l15 2-15 2z" />
                        </svg>
                    </button>
                </div>
                <div className="mt-2 text-center text-xs text-slate-400">
                    {t.chatDisclaimer || "JuriMaroc can make mistakes. Verify important info."}
                </div>
            </div>
        </div>
    )
}
